import { ReactNode } from "react";

/**
 * Per-course visual treatments. Wraps the lesson body so each course has a
 * distinct identity — not a copy-paste of the dark-amber default.
 *
 *  - staff-notation       → cream "manuscript paper" with pink staff lines
 *  - ear-training         → emerald background + scrolling sine waves
 *  - piano-foundation     → blue keyboard motif (stronger now: actual keys
 *                           rendered as background, not just stripes)
 *  - piano-intermediate   → cyan navy with metronome-arc motif
 *  - everything else      → default (handled at LessonShell level)
 *
 * Every wrapper is `overflow-x-clip`: the ornaments hang off the edges on purpose,
 * and on a 390px phone they made the whole lesson scroll sideways (MT34). Clip,
 * not hidden — hidden would turn the wrapper into a scroll box and break sticky.
 */

export interface CourseThemeProps {
  courseSlug: string;
  children: ReactNode;
}

export function CourseTheme({ courseSlug, children }: CourseThemeProps) {
  switch (courseSlug) {
    case "staff-notation":        return <StaffNotationTheme>{children}</StaffNotationTheme>;
    case "ear-training":          return <EarTrainingTheme>{children}</EarTrainingTheme>;
    case "piano-foundation":      return <PianoFoundationTheme>{children}</PianoFoundationTheme>;
    case "piano-intermediate":    return <PianoIntermediateTheme>{children}</PianoIntermediateTheme>;
    case "music-theory":          return <MusicTheoryTheme>{children}</MusicTheoryTheme>;
    case "south-indian-songbook": return <SouthIndianTheme>{children}</SouthIndianTheme>;
    case "ed-sheeran":            return <EdSheeranTheme>{children}</EdSheeranTheme>;
    case "sing-and-play":         return <SingAndPlayTheme>{children}</SingAndPlayTheme>;
    default: return <>{children}</>;
  }
}

/* -------- Staff Notation: cream manuscript with pink staff lines --------- */
function StaffNotationTheme({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-x-clip">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: `
            linear-gradient(180deg, rgba(254,243,237,0.06) 0%, rgba(254,243,237,0.02) 100%),
            repeating-linear-gradient(0deg, transparent 0 28px, rgba(244,63,94,0.08) 28px 29px)
          `,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-32"
        style={{
          background:
            "radial-gradient(700px 200px at 50% 0%, rgba(244,63,94,0.08), transparent 60%)",
        }}
      />
      {children}
    </div>
  );
}

/* -------- Ear Training: emerald + sine-wave overlay ---------------------- */
function EarTrainingTheme({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-x-clip">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(1100px 380px at 80% 10%, rgba(34,197,94,0.10), transparent 60%),
            radial-gradient(900px 320px at 10% 90%, rgba(16,185,129,0.08), transparent 60%)
          `,
        }}
      />
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-12 -z-10 h-24 w-full opacity-30"
        viewBox="0 0 1600 100"
        preserveAspectRatio="none"
      >
        <path
          d="M0,50 Q40,10 80,50 T160,50 T240,50 T320,50 T400,50 T480,50 T560,50 T640,50 T720,50 T800,50 T880,50 T960,50 T1040,50 T1120,50 T1200,50 T1280,50 T1360,50 T1440,50 T1520,50 T1600,50"
          fill="none"
          stroke="#22c55e"
          strokeOpacity="0.6"
          strokeWidth="2"
        />
        <path
          d="M0,50 Q60,90 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 T1560,50"
          fill="none"
          stroke="#10b981"
          strokeOpacity="0.4"
          strokeWidth="1.5"
        />
      </svg>
      {children}
    </div>
  );
}

/* -------- Piano Foundation: a *real* keyboard band at the top + paper -- */
function PianoFoundationTheme({ children }: { children: ReactNode }) {
  // Build a one-octave keyboard SVG that tiles along the top of the body.
  // Whites + blacks are accurate proportions. Subtle so it never competes
  // with content.
  const keyboardOctave = (
    <svg
      aria-hidden
      width="100%"
      height="48"
      viewBox="0 0 280 48"
      preserveAspectRatio="none"
    >
      {/* white keys */}
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <rect
          key={`w${i}`}
          x={i * 40}
          y="0"
          width="40"
          height="48"
          fill="#0f172a"
          stroke="#1e293b"
          strokeOpacity="0.6"
          strokeWidth="1"
        />
      ))}
      {/* black keys: positions = end of white keys 0,1,3,4,5 with width 24 */}
      {[26, 66, 146, 186, 226].map((x, i) => (
        <rect key={`b${i}`} x={x} y="0" width="28" height="30" fill="#1e293b" />
      ))}
    </svg>
  );

  return (
    <div className="relative overflow-x-clip">
      {/* keyboard band */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 opacity-30"
        style={{ height: 48 }}
      >
        <div className="flex h-full w-full">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-full flex-1">
              {keyboardOctave}
            </div>
          ))}
        </div>
      </div>
      {/* soft blue radial behind everything */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-20"
        style={{
          background:
            "radial-gradient(900px 320px at 50% -5%, rgba(59,130,246,0.10), transparent 60%)",
        }}
      />
      {/* practice-paper feel: very faint horizontal grid in the body */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-16 bottom-0 -z-10 opacity-25"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent 0 64px, rgba(59,130,246,0.06) 64px 65px)",
        }}
      />
      {children}
    </div>
  );
}

/* -------- Piano Intermediate: navy + metronome arm motif ----------------- */
function PianoIntermediateTheme({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-x-clip">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(1200px 400px at 50% 0%, rgba(6,182,212,0.10), transparent 65%)",
        }}
      />
      <svg
        aria-hidden
        className="pointer-events-none absolute right-4 top-12 -z-10 h-40 w-40 opacity-25"
        viewBox="0 0 200 200"
      >
        <path
          d="M100,180 L100,40 L70,170 Z"
          fill="none"
          stroke="#06b6d4"
          strokeWidth="1.5"
          strokeOpacity="0.7"
        />
        <circle cx="100" cy="180" r="6" fill="#06b6d4" />
        <line
          x1="100"
          y1="180"
          x2="120"
          y2="50"
          stroke="#06b6d4"
          strokeWidth="1"
          strokeOpacity="0.4"
        />
      </svg>
      {children}
    </div>
  );
}


/* -------- Music Theory: warm vellum + circle-of-fifths constellation ----- */
function MusicTheoryTheme({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-x-clip font-mt">
      {/* Warm parchment-like wash with subtle horizontal striations */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(900px 500px at 80% 0%, rgba(245,158,11,0.06), transparent 60%),
            radial-gradient(700px 400px at 10% 30%, rgba(168,85,247,0.05), transparent 60%),
            linear-gradient(180deg, rgba(250,243,224,0.04), rgba(250,243,224,0.01))
          `,
        }}
      />
      {/* A faint circle-of-fifths motif anchored top-right */}
      <svg
        aria-hidden
        viewBox="0 0 400 400"
        className="pointer-events-none absolute -right-16 -top-16 -z-10 h-[320px] w-[320px] opacity-[0.07]"
      >
        <circle cx="200" cy="200" r="160" fill="none" stroke="#f59e0b" strokeWidth="1.5" />
        <circle cx="200" cy="200" r="120" fill="none" stroke="#f59e0b" strokeWidth="0.8" />
        <circle cx="200" cy="200" r="80"  fill="none" stroke="#f59e0b" strokeWidth="0.5" />
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const x1 = 200 + Math.cos(a) * 80;
          const y1 = 200 + Math.sin(a) * 80;
          const x2 = 200 + Math.cos(a) * 160;
          const y2 = 200 + Math.sin(a) * 160;
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#f59e0b" strokeWidth="0.6" />
          );
        })}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const x = 200 + Math.cos(a) * 180;
          const y = 200 + Math.sin(a) * 180;
          return <circle key={"d"+i} cx={x} cy={y} r="3" fill="#f59e0b" opacity="0.6" />;
        })}
      </svg>
      {/* Subtle bottom-left constellation echo */}
      <svg
        aria-hidden
        viewBox="0 0 400 400"
        className="pointer-events-none absolute -bottom-16 -left-16 -z-10 h-[260px] w-[260px] opacity-[0.05]"
      >
        <circle cx="200" cy="200" r="140" fill="none" stroke="#a855f7" strokeWidth="1" />
        <circle cx="200" cy="200" r="90"  fill="none" stroke="#a855f7" strokeWidth="0.6" />
      </svg>
      {children}
    </div>
  );
}

/* -------- South Indian Songbook: warm saffron concert hall -------------- */
function SouthIndianTheme({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-x-clip">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(900px 420px at 92% -10%, rgba(217,119,6,0.10), transparent 60%),
            radial-gradient(700px 380px at -10% 80%, rgba(67,56,202,0.07), transparent 60%),
            radial-gradient(600px 300px at 50% 40%, rgba(217,119,6,0.04), transparent 60%)
          `,
        }}
      />
      <svg
        aria-hidden
        className="pointer-events-none absolute right-0 top-0 -z-10 h-64 w-64 opacity-[0.06]"
        viewBox="0 0 256 256"
      >
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i / 12) * Math.PI;
          const x2 = 256 + Math.cos(angle - Math.PI * 0.5) * 300;
          const y2 = 0 + Math.sin(angle - Math.PI * 0.5) * 300;
          return <line key={i} x1="256" y1="0" x2={x2} y2={y2} stroke="#d97706" strokeWidth="1" />;
        })}
        <circle cx="256" cy="0" r="40" fill="none" stroke="#d97706" strokeWidth="1" />
        <circle cx="256" cy="0" r="80" fill="none" stroke="#4338ca" strokeWidth="0.6" />
      </svg>
      {children}
    </div>
  );
}

/* -------- Ed Sheeran: clean acoustic, warm emerald undertone ------------- */
function EdSheeranTheme({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-x-clip">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(900px 350px at 60% -5%, rgba(16,185,129,0.08), transparent 60%),
            radial-gradient(600px 280px at 0% 80%, rgba(5,150,105,0.05), transparent 60%)
          `,
        }}
      />
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-32 w-full opacity-[0.06]"
        viewBox="0 0 1600 128"
        preserveAspectRatio="none"
      >
        {[20, 40, 60, 80, 100, 120].map((y) => (
          <line key={y} x1="0" y1={y} x2="1600" y2={y} stroke="#10b981" strokeWidth="0.8" />
        ))}
      </svg>
      {children}
    </div>
  );
}

/* -------- Sing & Play: warm purple/violet acoustic wash ------------------ */
function SingAndPlayTheme({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-x-clip">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(900px 400px at 20% -5%, rgba(168,85,247,0.09), transparent 60%),
            radial-gradient(700px 320px at 90% 60%, rgba(139,92,246,0.06), transparent 60%),
            radial-gradient(500px 250px at 50% 90%, rgba(167,139,250,0.05), transparent 60%)
          `,
        }}
      />
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-8 -z-10 h-20 w-full opacity-[0.08]"
        viewBox="0 0 1600 80"
        preserveAspectRatio="none"
      >
        <path
          d="M0,40 Q100,10 200,40 T400,40 T600,40 T800,40 T1000,40 T1200,40 T1400,40 T1600,40"
          fill="none" stroke="#a855f7" strokeWidth="1.5"
        />
        <path
          d="M0,40 Q150,65 300,40 T600,40 T900,40 T1200,40 T1500,40"
          fill="none" stroke="#8b5cf6" strokeWidth="1"
        />
      </svg>
      {children}
    </div>
  );
}
